"use client";

import Link from "next/link";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePageTitle } from "@/lib/usePageTitle";
import {
  BookIcon,
  TerminalIcon,
  SettingsIcon,
  ServerIcon,
  LinkIcon,
  UsersIcon,
  ArrowRightIcon,
  DownloadIcon,
  InfoIcon,
  ShieldIcon,
} from "@/components/icons";

export default function DocsPage() {
  usePageTitle("Documentation");
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="border-b border-border bg-foreground/5 py-12">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <BookIcon className="h-8 w-8" />
              </div>
              <div>
                <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
                  Plugin Documentation
                  <Badge variant="outline">v1.0.1</Badge>
                </h1>
                <p className="text-muted">
                  Complete guide for the BlockyNetwork Hytale plugin
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[250px_1fr]">
            {/* Sidebar Navigation */}
            <nav className="space-y-2">
              <p className="font-semibold text-foreground">Contents</p>
              <ul className="space-y-1 text-sm">
                <li>
                  <a href="#overview" className="text-muted hover:text-foreground">
                    Overview
                  </a>
                </li>
                <li>
                  <a href="#installation" className="text-muted hover:text-foreground">
                    Installation
                  </a>
                </li>
                <li>
                  <a href="#configuration" className="text-muted hover:text-foreground">
                    Configuration
                  </a>
                </li>
                <li>
                  <a href="#commands" className="text-muted hover:text-foreground">
                    Commands
                  </a>
                </li>
                <li>
                  <a href="#permissions" className="text-muted hover:text-foreground">
                    Permissions
                  </a>
                </li>
                <li>
                  <a href="#troubleshooting" className="text-muted hover:text-foreground">
                    Troubleshooting
                  </a>
                </li>
                <li>
                  <a href="#changelog" className="text-muted hover:text-foreground">
                    Changelog
                  </a>
                </li>
              </ul>
            </nav>

            {/* Main Content */}
            <div className="space-y-12">
              {/* Overview */}
              <section id="overview" className="scroll-mt-20">
                <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                  <InfoIcon className="h-6 w-6 text-primary" />
                  Overview
                </h2>
                <Card>
                  <CardContent className="prose prose-invert max-w-none pt-6">
                    <p className="text-muted">
                      The BlockyNetwork plugin connects your Hytale server to the
                      BlockyNetworks platform, enabling:
                    </p>
                    <ul className="mt-4 space-y-2">
                      <li className="flex items-start gap-2">
                        <LinkIcon className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>
                          <strong>Player Account Linking</strong> - Players can link
                          their in-game accounts to their BlockyNetworks profiles
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ServerIcon className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>
                          <strong>Server Organization Linking</strong> - Connect your
                          server to a BlockyNetworks organization
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <UsersIcon className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>
                          <strong>Real-time Player Tracking</strong> - Display live
                          player counts and online status on your server listing
                        </span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </section>

              {/* Installation */}
              <section id="installation" className="scroll-mt-20">
                <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                  <DownloadIcon className="h-6 w-6 text-primary" />
                  Installation
                </h2>
                <Card>
                  <CardContent className="pt-6">
                    <ol className="space-y-4">
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                          1
                        </span>
                        <div>
                          <p className="font-medium">Download the plugin</p>
                          <p className="text-sm text-muted">
                            Get the latest JAR file from the{" "}
                            <Link href="/downloads" className="text-primary hover:underline">
                              downloads page
                            </Link>
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                          2
                        </span>
                        <div>
                          <p className="font-medium">Install the plugin</p>
                          <p className="text-sm text-muted">
                            Place <code className="rounded bg-foreground/10 px-1.5 py-0.5">BlockyNetwork-1.0.1.jar</code>{" "}
                            in your server&apos;s <code className="rounded bg-foreground/10 px-1.5 py-0.5">mods/</code> folder
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                          3
                        </span>
                        <div>
                          <p className="font-medium">Start your server</p>
                          <p className="text-sm text-muted">
                            The plugin will generate a config file at{" "}
                            <code className="rounded bg-foreground/10 px-1.5 py-0.5">config/blockynetwork.json</code>
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                          4
                        </span>
                        <div>
                          <p className="font-medium">Configure the plugin</p>
                          <p className="text-sm text-muted">
                            Edit the config file with your BlockyNetworks details (see Configuration below)
                          </p>
                        </div>
                      </li>
                    </ol>
                  </CardContent>
                </Card>
              </section>

              {/* Configuration */}
              <section id="configuration" className="scroll-mt-20">
                <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                  <SettingsIcon className="h-6 w-6 text-primary" />
                  Configuration
                </h2>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">blockynetwork.json</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="overflow-x-auto rounded-lg bg-foreground/5 p-4 text-sm">
                      <code>{`{
  "convexHttpUrl": "",
  "serverId": "",
  "serverSecret": "",
  "serverName": "My Hytale Server"
}`}</code>
                    </pre>
                    <div className="mt-6 space-y-4">
                      <div>
                        <p className="font-medium">serverName</p>
                        <p className="text-sm text-muted">
                          The only field you need to set manually — the display name for your server
                          on BlockyNetworks, shown on your server listing page.
                        </p>
                      </div>
                      <div>
                        <p className="font-medium">convexHttpUrl, serverId, serverSecret</p>
                        <p className="text-sm text-muted">
                          These three fields are{" "}
                          <strong className="text-foreground">automatically populated</strong> when
                          you run{" "}
                          <code className="rounded bg-foreground/10 px-1.5 py-0.5">/link server</code>{" "}
                          in-game. You do not need to edit them manually.
                        </p>
                      </div>
                    </div>
                    <div className="mt-6 rounded-lg bg-primary/5 p-4">
                      <p className="text-sm text-muted">
                        <strong className="text-foreground">Tip:</strong> Set{" "}
                        <code className="rounded bg-foreground/10 px-1.5 py-0.5">serverName</code>{" "}
                        before linking — it will appear on your server&apos;s public profile
                        immediately after the link completes.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </section>

              {/* Commands */}
              <section id="commands" className="scroll-mt-20">
                <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                  <TerminalIcon className="h-6 w-6 text-primary" />
                  Commands
                </h2>
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <code className="rounded bg-primary/10 px-2 py-1 text-primary">
                          /link
                        </code>
                        <Badge variant="outline">Player Command</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="mb-4 text-muted">
                        Generates a 6-digit code for players to link their in-game account
                        to their BlockyNetworks profile.
                      </p>
                      <div className="rounded-lg bg-foreground/5 p-4">
                        <p className="text-sm font-medium">Usage</p>
                        <code className="text-sm text-muted">/link</code>
                        <p className="mt-2 text-sm font-medium">Response</p>
                        <code className="text-sm text-muted">
                          Your link code: ABC123 (expires 12:30:00)
                        </code>
                      </div>
                      <p className="mt-4 text-sm text-muted">
                        Players enter this code on the BlockyNetworks website under
                        Settings → Link Game Account.
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <code className="rounded bg-primary/10 px-2 py-1 text-primary">
                          /link server
                        </code>
                        <Badge variant="outline">Admin Command</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="mb-4 text-muted">
                        Generates a code to link this Hytale server to a BlockyNetworks
                        organization. Requires the <code className="rounded bg-foreground/10 px-1.5 py-0.5">blockynetwork.linkserver</code> permission.
                      </p>
                      <div className="rounded-lg bg-foreground/5 p-4">
                        <p className="text-sm font-medium">Usage</p>
                        <code className="text-sm text-muted">/link server</code>
                        <p className="mt-2 text-sm font-medium">Response</p>
                        <code className="text-sm text-muted">
                          Server link code: XYZ789 (expires 12:30:00)
                        </code>
                      </div>
                      <p className="mt-4 text-sm text-muted">
                        Enter this code in your Organization Settings → Link Game Server
                        on the BlockyNetworks website.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </section>

              {/* Permissions */}
              <section id="permissions" className="scroll-mt-20">
                <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                  <ShieldIcon className="h-6 w-6 text-primary" />
                  Permissions
                </h2>
                <Card>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="pb-3 text-left font-medium">Permission</th>
                            <th className="pb-3 text-left font-medium">Description</th>
                            <th className="pb-3 text-left font-medium">Default</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-border">
                            <td className="py-3">
                              <code className="rounded bg-foreground/10 px-1.5 py-0.5 text-xs">
                                blockynetwork.linkserver
                              </code>
                            </td>
                            <td className="py-3 text-muted">
                              Allows use of /link server command
                            </td>
                            <td className="py-3 text-muted">op</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-4 text-sm text-muted">
                      The <code className="rounded bg-foreground/10 px-1.5 py-0.5">/link</code> command
                      has no permission requirement - all players can link their accounts.
                    </p>
                  </CardContent>
                </Card>
              </section>

              {/* Troubleshooting */}
              <section id="troubleshooting" className="scroll-mt-20">
                <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                  <InfoIcon className="h-6 w-6 text-primary" />
                  Troubleshooting
                </h2>
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        &quot;Server not linked&quot;
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted">
                        Your server hasn&apos;t been linked to a BlockyNetworks organization yet.
                        Run <code className="rounded bg-foreground/10 px-1.5 py-0.5">/link server</code>{" "}
                        in-game and enter the code on the BlockyNetworks website.
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        &quot;Failed to generate link code&quot;
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted">
                        Check your server logs for detailed error messages. Common causes:
                      </p>
                      <ul className="mt-2 list-inside list-disc text-sm text-muted">
                        <li>Network connectivity issues</li>
                        <li>BlockyNetworks service temporarily unavailable</li>
                        <li>Firewall blocking outbound connections</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Link code expired
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted">
                        Link codes are valid for 10 minutes. Simply run the command again
                        to generate a new code.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </section>

              {/* Changelog */}
              <section id="changelog" className="scroll-mt-20">
                <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                  <InfoIcon className="h-6 w-6 text-primary" />
                  Changelog
                </h2>
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        v1.0.1
                        <Badge variant="outline">Latest</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1 text-sm text-muted">
                        <li>• Bug fixes and improved connection stability</li>
                        <li>• Improved heartbeat reliability — reduced false disconnects</li>
                        <li>• Minor performance improvements to player tracking</li>
                      </ul>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">v1.0.0</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1 text-sm text-muted">
                        <li>• Initial release</li>
                        <li>• Player account linking via /link</li>
                        <li>• Server organization linking via /link server</li>
                        <li>• Real-time player presence and online status</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </section>

              {/* CTA */}
              <section className="border-t border-border pt-12">
                <div className="text-center">
                  <h3 className="mb-4 text-xl font-bold">Need more help?</h3>
                  <p className="mb-6 text-muted">
                    Join our community for support and updates.
                  </p>
                  <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                    <Link href="/downloads">
                      <Button className="gap-2">
                        <DownloadIcon className="h-4 w-4" />
                        Download Plugin
                      </Button>
                    </Link>
                    <Link href="/servers">
                      <Button variant="outline" className="gap-2">
                        Explore Servers
                        <ArrowRightIcon className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
